import React from 'react';
import Table, { TableColumn } from '../../components/Table/Table';
import { FilterBar } from '../../components/Common';
import { FilterConfig } from '../../components/Common/FilterBar';
import { CandidateEvaluation, getProgressBarClass, getStatusBadgeClass } from './evaluationTypes';

interface EvaluationListProps {
  evaluations: CandidateEvaluation[];
  filteredEvaluations: CandidateEvaluation[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  onAdd: () => void;
  onView: (evaluation: CandidateEvaluation) => void;
  onExport: () => void;
  onPrint: () => void;
}

const columns: TableColumn[] = [
  { key: 'candidateName', label: 'Candidate Name', sortable: true },
  { key: 'jobTitle', label: 'Job Title', sortable: true },
  { key: 'evaluationDate', label: 'Evaluation Date', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
  { key: 'evaluatorName', label: 'Evaluator', sortable: true },
  {
    key: 'percentage',
    label: 'Score',
    sortable: true,
    render: (value: number, item: CandidateEvaluation) => (
      <div>
        <div className="flex items-center">
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-900">{value.toFixed(1)}%</div>
            <div className="text-xs text-gray-500">{item.totalScore}/{item.maxScore} points</div>
          </div>
        </div>
        <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full ${getProgressBarClass(value)}`}
            style={{ width: `${value}%` }}
          />
        </div>
      </div>
    )
  },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    render: (value: string) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(value)}`}>
        {value.charAt(0).toUpperCase() + value.slice(1)}
      </span>
    )
  }
];

const EvaluationList: React.FC<EvaluationListProps> = ({
  evaluations, filteredEvaluations,
  searchTerm, onSearchChange,
  statusFilter, onStatusFilterChange,
  onAdd, onView, onExport, onPrint,
}) => {
  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      value: statusFilter,
      onChange: onStatusFilterChange,
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'excellent', label: 'Excellent' },
        { value: 'good', label: 'Good' },
        { value: 'average', label: 'Average' },
        { value: 'unsatisfactory', label: 'Unsatisfactory' },
      ],
    },
  ];

  const handleClearFilters = () => {
    onSearchChange('');
    onStatusFilterChange('all');
  };

  return (
    <>
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        searchPlaceholder="Search evaluations..."
        filters={filters}
        onClearFilters={handleClearFilters}
        totalCount={evaluations.length}
        filteredCount={filteredEvaluations.length}
        entityName="Evaluations List"
        onAdd={onAdd}
        addLabel="New Evaluation"
        onExport={onExport}
        onPrint={onPrint}
      />
      <Table
        data={filteredEvaluations}
        columns={columns}
        onEdit={onView}
        showAddButton={false}
        searchable={false}
      />
    </>
  );
};

export default EvaluationList;
